import React from 'react';
import { Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

export interface FilterChip {
  key: string;
  group: string;
  label: string;
  onRemove: () => void;
}

interface ResultsToolbarProps {
  searchId: string;
  searchLabel: string;
  placeholder: string;
  query: string;
  onQuery: (value: string) => void;
  count?: React.ReactNode;
  middle?: React.ReactNode;
  filterCount: number;
  filterLabel?: string;
  // Left out where filters don't apply: no Filters button.
  onOpenFilters?: () => void;
  actions?: React.ReactNode;
  onReset: () => void;
  resetLabel: string;
  resetDisabled?: boolean;
  chips: FilterChip[];
  onClearFilters: () => void;
}

const NOTHING_TO_RESET = 'Nothing to reset';

const ResultsToolbar: React.FC<ResultsToolbarProps> = ({
  searchId,
  searchLabel,
  placeholder,
  query,
  onQuery,
  count,
  middle,
  filterCount,
  filterLabel,
  onOpenFilters,
  actions,
  onReset,
  resetLabel,
  resetDisabled = false,
  chips,
  onClearFilters,
}) => {
  const resetHintId = React.useId();
  return (
    <>
      <div id='psc-results' className='psc-toolbar psc-settle'>
        <div className='psc-search'>
          <span className='glyph'>
            <SearchIcon sx={{ fontSize: 18 }} />
          </span>
          <label htmlFor={searchId} className='psc-sr'>
            {searchLabel}
          </label>
          <input
            id={searchId}
            type='search'
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder={placeholder}
          />
        </div>
        {count}
        {middle}
        <span className='psc-spacer' />
        {onOpenFilters && (
          <button
            type='button'
            className={`psc-btn psc-filterbtn${filterCount ? ' is-set' : ''}`}
            onClick={onOpenFilters}
            aria-haspopup='dialog'
          >
            <FilterListIcon sx={{ fontSize: 18 }} />
            Filters
            {filterLabel && (
              <span className='psc-filterbtn-name' title={filterLabel}>
                · {filterLabel}
              </span>
            )}
            {filterCount > 0 && (
              <span className='psc-badge'>{filterCount}</span>
            )}
          </button>
        )}
        {actions}
        {/* A disabled button fires no mouse events, so the span carries the tooltip. */}
        <Tooltip
          title={resetDisabled ? NOTHING_TO_RESET : resetLabel}
          describeChild
        >
          <span className='psc-tipwrap'>
            <button
              type='button'
              className='psc-btn psc-btn--icon'
              onClick={onReset}
              disabled={resetDisabled}
              aria-label={resetLabel}
              aria-describedby={resetDisabled ? resetHintId : undefined}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </button>
          </span>
        </Tooltip>
        {resetDisabled && (
          <span id={resetHintId} className='psc-sr'>
            {NOTHING_TO_RESET}
          </span>
        )}
      </div>

      {chips.length > 0 && (
        <div className='psc-chips'>
          {chips.map((c) => (
            <span key={c.key} className='psc-fchip'>
              <span className='g'>{c.group}</span>
              {c.label}
              <button
                type='button'
                className='psc-x'
                onClick={c.onRemove}
                aria-label={`Remove filter ${c.label}`}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            </span>
          ))}
          <button
            type='button'
            className='psc-btn psc-btn--ghost psc-chips-clear'
            onClick={onClearFilters}
          >
            Clear all
          </button>
        </div>
      )}
    </>
  );
};

export default ResultsToolbar;
